import { useCallback, useEffect, useMemo, useState } from "react"
import {
  DEFAULT_REPORT_ID,
  DEFAULT_REPORT_NAME,
  parseReportConfig,
  type ReportConfig,
} from "~/shared/kpi"
import {
  useSavedReportControllerListReportsQuery,
  type SavedReportDto,
} from "~/ui-rtk/api/savedReportApi"

const ACTIVE_REPORT_STORAGE_KEY = "kpi-active-report-id"

export type ReportOption = {
  id: string
  name: string
  isDefault: boolean
  isPrivate: boolean
}

/**
 * Owns the "which report is selected" state for the KPI dashboard: fetches the
 * company's saved reports, tracks the active selection (persisted in
 * localStorage), and resolves the active report's parsed config.
 *
 * `activeConfig` is `null` for the builtin Default report — in that case the
 * dashboard drives its group order from the user's localStorage table order
 * (legacy behavior). For a custom report it is the parsed `ReportConfig`.
 */
export function useKpiReports() {
  const { data: reports = [], isLoading: isLoadingReports } =
    useSavedReportControllerListReportsQuery()

  const [activeReportId, setActiveReportIdState] = useState<string>(() => {
    if (typeof window === "undefined") {
      return DEFAULT_REPORT_ID
    }
    return localStorage.getItem(ACTIVE_REPORT_STORAGE_KEY) || DEFAULT_REPORT_ID
  })

  // If the persisted report no longer exists (deleted, or it belonged to a
  // different company), fall back to the default report.
  useEffect(() => {
    if (activeReportId === DEFAULT_REPORT_ID || isLoadingReports) {
      return
    }
    if (!reports.some(report => report.id === activeReportId)) {
      setActiveReportIdState(DEFAULT_REPORT_ID)
    }
  }, [activeReportId, reports, isLoadingReports])

  const setActiveReportId = useCallback((id: string) => {
    setActiveReportIdState(id)
    if (typeof window !== "undefined") {
      localStorage.setItem(ACTIVE_REPORT_STORAGE_KEY, id)
    }
  }, [])

  const options = useMemo<ReportOption[]>(
    () => [
      {
        id: DEFAULT_REPORT_ID,
        name: DEFAULT_REPORT_NAME,
        isDefault: true,
        isPrivate: false,
      },
      ...reports.map(report => ({
        id: report.id,
        name: report.name,
        isDefault: false,
        isPrivate: report.isPrivate,
      })),
    ],
    [reports],
  )

  const activeReport = useMemo<SavedReportDto | null>(
    () => reports.find(report => report.id === activeReportId) ?? null,
    [reports, activeReportId],
  )

  const activeConfig = useMemo<ReportConfig | null>(() => {
    if (activeReportId === DEFAULT_REPORT_ID || !activeReport) {
      return null
    }
    return parseReportConfig(activeReport.config)
  }, [activeReportId, activeReport])

  return {
    options,
    reports,
    isLoadingReports,
    activeReportId,
    setActiveReportId,
    activeReport,
    activeConfig,
    isDefaultActive: activeReportId === DEFAULT_REPORT_ID,
  }
}
